export type ManagedUser = {
  uid: string;
  email: string;
  whitelisted: boolean;
  createdAt: string | null;
  linkedMemberId: string | null;
};

export type WhitelistStatus = 'unknown' | 'loading' | 'allowed' | 'pending';

export type AppView = 'home' | 'admin';

export type WeightHomeHighlight = {
  memberName: string;
  memberImageUrl: string;
  afterYear: number;
  previousYear: number;
  beforeWeight: number;
  afterWeight: number;
  difference: number;
};

export type WeightHomeGainHighlight = {
  memberName: string;
  memberImageUrl: string;
  year: number;
  beforeWeight: number;
  afterWeight: number;
  difference: number;
};

export type WeightPastWinner = WeightHomeHighlight;

export type WeightWinnerPoint = {
  memberId: string;
  afterYear: number;
};

export type ListUsersResponse = {
  users: ManagedUser[];
};

export type MemberRole = 'formand' | 'kasserer' | 'ovrige';

export type Member = {
  id: string;
  navn: string;
  rolle: MemberRole;
  beskrivelse: string;
  billedeUrl: string;
  billedePath?: string;
  oprettetAf: string;
  oprettetAt: string | null;
};

export const MEMBER_ROLE_OPTIONS: Array<{ value: MemberRole; label: string }> = [
  { value: 'formand', label: 'Formand' },
  { value: 'kasserer', label: 'Kasserer' },
  { value: 'ovrige', label: 'Øvrige medlemmer' },
];

export type CalendarEventDoc = {
  id: string;
  title: string;
  start: string;
  end: string | null;
  allDay: boolean;
  location: string;
  description: string;
  createdBy: string;
};

export type WeightEntryDoc = {
  personId: string;
  weight: number;
  measuredAt: string;
  measuredYear?: number;
  periodLabel?: string;
  periodOrder?: number;
  createdBy: string;
};

export type WeightEntry = {
  id: string;
  personId: string;
  weight: number;
  measuredAt: string;
  measuredYear: number;
  periodLabel: string;
  periodOrder: number;
  createdBy: string;
};

export const WEIGHT_PERIOD_OPTIONS = [
  { value: 'before', label: 'Før Julefrokost', order: 1 },
  { value: 'after', label: 'Efter Julefrokost', order: 2 },
] as const;

export type WeightPeriodValue = (typeof WEIGHT_PERIOD_OPTIONS)[number]['value'];

export type MinuteEntry = {
  id: string;
  title: string;
  meetingDate: string;
  content: string;
  createdBy: string;
  createdAt: string | null;
};

export type MinuteEntryDoc = {
  title?: string;
  meetingDate?: string;
  content?: string;
  createdBy?: string;
  createdAt?: { toDate: () => Date };
};

export type SuggestionCategory = 'vedtaegt' | 'generalforsamling' | 'andet';

export const SUGGESTION_CATEGORY_OPTIONS: Array<{ value: SuggestionCategory; label: string }> = [
  { value: 'vedtaegt', label: 'Ændring af vedtægter' },
  { value: 'generalforsamling', label: 'Punkt til generalforsamlingen' },
  { value: 'andet', label: 'Andet' },
];

export const getSuggestionCategoryLabel = (category: SuggestionCategory): string =>
  SUGGESTION_CATEGORY_OPTIONS.find((option) => option.value === category)?.label || 'Forslag';

export type SuggestionEntry = {
  id: string;
  title: string;
  body: string;
  category: SuggestionCategory;
  createdBy: string;
  createdByEmail: string;
  authorName: string;
  createdAt: string | null;
  updatedAt: string | null;
};

export type SuggestionDoc = {
  title?: string;
  body?: string;
  category?: string;
  createdBy?: string;
  createdByEmail?: string;
  authorName?: string;
  createdAt?: { toDate: () => Date };
  updatedAt?: { toDate: () => Date };
};

export type GalleryFolder = {
  id: string;
  name: string;
  createdBy: string;
  createdAt: string | null;
};

export type GalleryFolderDoc = {
  name?: string;
  createdBy?: string;
  createdAt?: { toDate: () => Date };
};

export type GalleryImage = {
  id: string;
  folderId: string;
  fileName: string;
  url: string;
  path: string;
  thumbUrl?: string;
  thumbPath?: string;
  optimized?: boolean;
  uploadedBy: string;
  createdAt: string | null;
};

export type GalleryImageDoc = {
  folderId?: string;
  fileName?: string;
  url?: string;
  path?: string;
  thumbUrl?: string;
  thumbPath?: string;
  optimized?: boolean;
  uploadedBy?: string;
  createdAt?: { toDate: () => Date };
};

export type VedtaegtSection = {
  id: string;
  text: string;
  children: VedtaegtSection[];
};

export type VedtaegtDoc = {
  title?: string;
  sections?: unknown;
  updatedBy?: string;
  updatedAt?: { toDate: () => Date };
};
